import React, { useState, useEffect } from 'react';
import { Table, Input, Space, DatePicker, Typography, Tag } from 'antd';
const { Title, Text } = Typography;

import { SearchOutlined, UserSwitchOutlined } from '@ant-design/icons';
import io from 'socket.io-client';

import API from '../../API/API';
import { useNavigate } from 'react-router-dom';
import Config from '../../API/Config';
const AllLeads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);



  const fetchLeads = async () => {
    setLoading(true);

    try {
      const userData = JSON.parse(localStorage.getItem("userData"));
      if (!userData?.id) {
        console.error("User authentication data not found");
        setLoading(false);
        return;
      }



      const params = {

        page: currentPage,
        limit: pageSize,
      };
      // Include search parameter if available
      if (searchTerm?.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await API.leadSearch(params);

      if (response?.status) {
        setLeads(response?.data);
        setTotalRecords(response.pagination.totalRecords);
      } else {
        console.error("Invalid response format:", response);
        setLeads([]);
      }

    } catch (error) {
      console.error('Failed to fetch leads:', error);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      fetchLeads();
    }, 500); // Delays API call by 500ms to allow typing

    return () => clearTimeout(delaySearch);
  }, [currentPage, pageSize, searchTerm]);


  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };


  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userData"));
    const currentUserId = userData?.id;

    if (!currentUserId) {
      console.error("No user ID found for socket connection");
      return;
    }

    console.log("Initializing socket connection for MyLeads, user:", currentUserId);

    const socket = io(Config.baseURL, {
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      transports: ['websocket', 'polling']
    });

    socket.on('connect', () => {
      console.log('✅ MyLeads socket connected');
      // Join user-specific room
      socket.emit('join', currentUserId);
    });

    socket.on('disconnect', (reason) => {
      console.log('❌ MyLeads socket disconnected:', reason);
    });

    socket.on('connect_error', (err) => {
      console.error('❌ MyLeads socket connection error:', err);
    });

    const handleLeadAssigned = (newLead) => {
      console.log('📨 New lead assigned event received:', newLead);
      console.log('Current user ID:', currentUserId);
      console.log('Lead agent ID:', newLead.agentId);
      console.log('Match check:', String(newLead.agentId) === String(currentUserId));

      // Only update leads list if the lead is assigned to current user
      if (String(newLead.agentId) === String(currentUserId)) {
        console.log('✅ Adding lead to current user\'s list');

        setLeads(prevLeads => {
          // Check if lead already exists to prevent duplicates
          const leadExists = prevLeads.some(lead => lead._id === newLead.lead._id);

          if (!leadExists) {
            console.log('➕ Adding new lead to list:', newLead.lead._id);
            return [newLead.lead, ...prevLeads];
          } else {
            console.log('⚠️ Lead already exists in list, skipping:', newLead.lead._id);
            return prevLeads;
          }
        });

        // Update total records count
        setTotalRecords(prev => prev + 1);
      } else {
        console.log('ℹ️ Lead assigned to different user, ignoring');
      }
    };

    const handleLeadQueued = (queuedLead) => {
      console.log('📬 Lead queued event received:', queuedLead);
      // You might want to show a notification that a lead was queued
      // but don't add it to the current user's leads list since it's unassigned
    };

    // Register event listeners
    socket.on('lead-assigned', handleLeadAssigned);
    socket.on('lead-queued', handleLeadQueued);

    // Cleanup function
    return () => {
      console.log('🧹 Cleaning up MyLeads socket connection');
      socket.off('lead-assigned', handleLeadAssigned);
      socket.off('lead-queued', handleLeadQueued);
      socket.disconnect();
    };
  }, []); // Empty dependency array since we want this to run once on mount



  const leadStageColors = {
    'New': { color: '#1890ff', background: '#e6f7ff' }, // Cool blue, fresh lead
    'Call Back': { color: '#faad14', background: '#fffbe6' }, // Amber for pending action
    'Voice Mail': { color: '#9254de', background: '#f9f0ff' }, // Light purple, neutral waiting
    'Follow-Up': { color: '#fa8c16', background: '#fff7e6' }, // Orange, action needed
    'Quoted': { color: '#13c2c2', background: '#e6fffb' }, // Teal, info shared
    'Negotiation': { color: '#eb2f96', background: '#fff0f6' }, // Pink, active dialogue
    'Pending Payment': { color: '#fa541c', background: '#fff2e8' }, // Bold orange-red, urgency
    'Payment Completed': { color: '#52c41a', background: '#f6ffed' }, // Green, success
    'Sales Made': { color: '#389e0d', background: '#f6ffed' }, // Deeper green, closed deal
    'Part Not Available': { color: '#bfbfbf', background: '#fafafa' }, // Neutral gray
    'Wrong Number': { color: '#ff4d4f', background: '#fff1f0' }, // Red, error
    'Not Interested': { color: '#d46b08', background: '#fff7e6' }, // Muted orange, closed
    'Hang Up': { color: '#8c8c8c', background: '#f5f5f5' }, // Dark gray, dead
    'Fake': { color: '#a8071a', background: '#fff1f0' }, // Deep red, invalid
    'Test Lead': { color: '#7cb305', background: '#fcffe6' }, // Lime, internal
  };
  function formatToPST(isoDateString) {
    const date = new Date(isoDateString);


    const options = {
      timeZone: 'America/Los_Angeles',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    };

    return date.toLocaleString('en-US', options);
  }
  const columns = [


    { title: "Sales-Agent", dataIndex: "agentName", key: "agentName" },

    {
      title: "Customer Name",
      dataIndex: ["personalInfo", "fullName"],
      key: "fullName",
      render: (text, record) => (
        <div>
          <span>{record.personalInfo.fullName}</span>

        </div>
      )
    },
    {
      title: "Customer Number",
      dataIndex: ["personalInfo", "phoneNumber"],
      key: "phoneNumber",
      render: (text, record) => (
        <div>
          <span>{record.personalInfo.phoneNumber}</span>

        </div>
      )
    },
    {
      title: "Customer Mail",
      dataIndex: ["personalInfo", "email"],
      key: "email",
      render: (text, record) => (
        <div>
          <span>{record.personalInfo.email}</span>


        </div>
      )
    },
    {
      title: "Customer ZIP Code",
      dataIndex: ["personalInfo", "phoneNumber"],
      key: "phoneNumber",
      render: (text, record) => (
        <div>
          <span>{record.personalInfo.zipCode}</span>

        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'leadStage',
      key: 'leadStage',
      width: 120,
      render: (stage) => {
        const styleConfig = leadStageColors[stage] || {
          color: '#000000',
          background: '#f5f5f5',
        };

        return (
          <Tag
            style={{
              color: styleConfig.color,
              backgroundColor: styleConfig.background,
              borderRadius: '8px',
              fontWeight: 500,
              padding: '0 12px',
            }}
          >
            {stage}
          </Tag>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'leadType',
      key: 'leadType',
      width: 120,
      render: (type) => {
        let color = '';
        let style = {
          borderRadius: '8px',
          fontWeight: 500,
          padding: '0 12px',
        };

        switch (type?.toLowerCase()) {
          case 'inbound':
            color = '#52c41a'; // green
            return (
              <Tag color="green" style={style}>
                INBOUND
              </Tag>
            );

          case 'outbound':
            color = '#1890ff'; // blue
            return (
              <Tag color="blue" style={style}>
                OUTBOUND
              </Tag>
            );

          default:
            return (
              <Tag color="default" style={style}>
                {type?.toUpperCase() || 'UNKNOWN'}
              </Tag>
            );
        }
      },
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => formatToPST(date)
    },


  ];


  return (
    <div >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <Space direction="vertical" size={2}>
          <Title level={2} style={{ fontWeight: "bold", margin: 0 }}>Search Leads</Title>
          <Text type="secondary">Search and act on every lead with speed and precision.</Text>
        </Space>


        <Space>

          <Input
            type="text"
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => {
              console.log("Search Input Changed:", e.target.value); // ✅ Debug log
              setSearchTerm(e.target.value);
            }}
            allowClear
            suffix={<SearchOutlined />}
          />


        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={leads}
        rowKey={(record) => record._id || record.key}
        loading={loading}
        // pagination={{
        //   current: currentPage,
        //   pageSize: pageSize,
        //   total: totalRecords,
        //   showSizeChanger: true,
        //   pageSizeOptions: ["10", "20", "50", "100"],
        //   showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} entries`,
        //   position: ['topRight'],
        // }}
        pagination={false}
        onChange={handleTableChange}
        scroll={{
          x: 'max-content',
          y: '55vh'
        }}
        locale={{
          emptyText: !searchTerm
            ? "No leads available yet."
            : "No leads found for your search.",
        }}

      />


    </div>
  );
};

export default AllLeads;


